<script setup>
import ButtonCta from '@/Components/ui/buttons/ButtonCta.vue'
import DatePicker from '@/Components/ui/DatePicker.vue'
import FormSelectbox from '@/Components/ui/FormSelectbox.vue'
import InputBox from '@/Components/ui/InputBox.vue'
import {
	threeMonthsLater,
	tomorrow,
	dayAfterTomorrow,
} from '@/Config/bookingRules'
import { useBooking } from '@/Stores/booking.js'
import { nextTick, onBeforeMount, onMounted, ref, computed } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'
import { useFetchSearch } from '@/Composables/useFetchApi.js'
import { formatDateFromNumeric } from '@/Composables/common.js'

const booking = useBooking()

const pickupLocationRef = ref(null)
const pickupTimeRef = ref(null)
const dropoffLocationRef = ref(null)
const dropoffTimeRef = ref(null)
const ageRef = ref(null)
const campaigncodeRef = ref(null)
const pickupDateRef = ref(null)
const dropoffDateRef = ref(null)

const pickupDate = computed(() => {
	if (payload.pickupdate) {
		return new Date(payload.pickupdate)
	}
	return null
})

const pickupDatePlusOne = computed(() => {
	if (payload.pickupdate) {
		const nextDay = new Date(pickupDate.value)
		nextDay.setDate(pickupDate.value.getDate() + 1)
		return nextDay
	}
	return dayAfterTomorrow
})

const highlightedDate = computed(() => {
	return payload.pickupdate ? new Date(payload.pickupdate) : null
})

const resetAll = () => {
	booking.reset()
	pickupLocationRef.value.reset()
	pickupTimeRef.value.reset()
	dropoffLocationRef.value.reset()
	dropoffTimeRef.value.reset()
	ageRef.value.reset()
	pickupDateRef.value = ''
	dropoffDateRef.value = ''
	campaigncodeRef.value = ''
}

const pickupTimeOptions = computed(() => {
	let selectedLocation = payload.pickuplocationid || null

	// Find the location dynamically
	const location = booking.pickup.locations.find(
		(loc) => loc.id === selectedLocation
	)

	// Return the time_slots if found, otherwise return an empty array
	return location ? location.time_slots : []
})

const dropoffTimeOptions = computed(() => {
	let selectedLocation = payload.dropofflocationid || null

	// Find the location dynamically
	const location = booking.pickup.locations.find(
		(loc) => loc.id === selectedLocation
	)

	// Return the time_slots if found, otherwise return an empty array
	return location ? location.time_slots : []
})

const payload = useForm({
	pickuplocationid: '',
	pickupdate: '',
	pickuptime: '',
	dropofflocationid: '',
	dropoffdate: '',
	dropofftime: '',
	ageid: '',
	campaigncode: '',
})

const isFormInvalid = computed(() => {
	if (booking.getIsFirstSearchComplete) {
		return false
	} else {
		return (
			!payload.pickuplocationid ||
			!payload.pickupdate ||
			!payload.pickuptime ||
			!payload.dropofflocationid ||
			!payload.dropoffdate ||
			!payload.dropofftime ||
			!payload.ageid
		)
	}
})

const page = usePage()

const onSubmit = async () => {
	payload.clearErrors()

	if (page.url === '/') {
		booking.isFromHome = true
	}

	booking.isSubmitting = true
	booking.updateButtonState()

	// Programmatically select dates -
	// Required to get datepicker to punch in the dates, else will cause error.
	pickupDateRef.value.handleSelectDate(booking.getSelectedPickup.date)
	dropoffDateRef.value.handleSelectDate(booking.getSelectedDropoff.date)

	booking.mapValues(payload)

	await useFetchSearch(payload)
}

const handleSelectSameDropoff = (e) => {
	dropoffLocationRef.value.select(e)
	payload.dropofflocationid = e
}

const handleSelectSamePickup = (e) => {
	pickupLocationRef.value.select(e)
	payload.pickuplocationid = e
}

const paramsObj = ref({})
const hasParams = ref(false)

onBeforeMount(async () => {
	const allowedParams = new Set([
		'ageid',
		'campaigncode',
		'dropoffdate',
		'dropofftime',
		'dropofflocationid',
		'pickupdate',
		'pickuptime',
		'pickuplocationid',
	])

	const params = new URLSearchParams(window.location.search)
	hasParams.value = [...allowedParams].every(
		(key) => params.get(key) !== null
	)

	if (hasParams.value) {
		paramsObj.value = {
			ageid: params.get('ageid'),
			campaigncode: params.get('campaigncode'),
			dropoffdate: formatDateFromNumeric(params.get('dropoffdate')),
			dropofftime: params.get('dropofftime'),
			dropofflocationid: Number(params.get('dropofflocationid')),
			pickupdate: formatDateFromNumeric(params.get('pickupdate')),
			pickuptime: params.get('pickuptime'),
			pickuplocationid: Number(params.get('pickuplocationid')),
		}
	}
})

const prefillPromocode = import.meta.env.VITE_PROMOCODE || null
const prefillBookingDetails = () => {
	ageRef.value.onSelect(booking.getDefaultAge?.id)
	if (prefillPromocode) {
		payload.campaigncode = prefillPromocode
	}
}

onMounted(async () => {
	nextTick(() => {
		const {
			ageid,
			campaigncode,
			dropoffdate,
			dropofftime,
			dropofflocationid,
			pickupdate,
			pickuptime,
			pickuplocationid,
		} = paramsObj.value

		// Prefill form fields with selected values from home page.
		if (!hasParams.value) {
			resetAll()
			nextTick(() => {
				prefillBookingDetails()
			})
		} else {
			nextTick(async () => {
				payload.campaigncode = campaigncode
				payload.pickuplocationid = Number(pickuplocationid)
				payload.dropofflocationid = Number(dropofflocationid)
				payload.ageid = Number(ageid)
				payload.pickupdate = pickupdate
				payload.dropoffdate = dropoffdate
				payload.dropofftime = dropofftime
				payload.pickuptime = pickuptime

				pickupLocationRef.value.onSelect(pickuplocationid)
				dropoffLocationRef.value.onSelect(dropofflocationid)
				ageRef.value.onSelect(Number(ageid))
				pickupDateRef.value.handleDate(pickupdate)
				dropoffDateRef.value.handleDate(dropoffdate)
				dropoffTimeRef.value.onSelect(dropofftime)
				pickupTimeRef.value.onSelect(pickuptime)

				booking.mapValues(payload)
				booking.updateButtonState()
			})
		}
	})
})
</script>

<template>
	<div class="flex flex-col gap-6">
		<form @submit.prevent="onSubmit">
			<div class="w-full">
				<h3>Pick up</h3>
				<div class="flex flex-col lg:flex-row gap-2 justify-between">
					<FormSelectbox
						ref="pickupLocationRef"
						key="pickup-location-input"
						v-model="payload.pickuplocationid"
						label-name="Pick up location"
						:error="payload.errors.pickuplocationid"
						:show-label="false"
						:options="booking.pickup.locations"
						@update:model-value="handleSelectSameDropoff"
					/>
					<DatePicker
						ref="pickupDateRef"
						key="pickup-date-input"
						v-model="payload.pickupdate"
						place-holder="Pick up date"
						:show-input="false"
						:allow-text-input="false"
						:error="payload.errors.pickupdate"
						:fancy-format="true"
						:min-date="tomorrow"
						:max-date="threeMonthsLater"
						:multi-calendar="true"
					/>
					<FormSelectbox
						ref="pickupTimeRef"
						key="pickup-time-input"
						v-model="payload.pickuptime"
						label-name="Pick up time"
						:error="payload.errors.pickuptime"
						:show-label="false"
						:options="pickupTimeOptions"
						max-height="max-h-[260px]"
					/>
				</div>
			</div>
			<div class="w-full mt-4">
				<h3>Drop off</h3>
				<div class="flex flex-col lg:flex-row gap-2">
					<FormSelectbox
						ref="dropoffLocationRef"
						key="dropoff-location-input"
						v-model="payload.dropofflocationid"
						label-name="Drop off location"
						:error="payload.errors.dropofflocationid"
						:show-label="false"
						:options="booking.dropoff.locations"
						@update:model-value="handleSelectSamePickup"
					/>
					<DatePicker
						ref="dropoffDateRef"
						key="dropoff-date-input"
						v-model="payload.dropoffdate"
						place-holder="Drop off date"
						:show-input="false"
						:error="payload.errors.dropoffdate"
						:fancy-format="true"
						:min-date="pickupDatePlusOne"
						:max-date="threeMonthsLater"
						:multi-calendar="true"
						:highlight-date="highlightedDate"
					/>
					<FormSelectbox
						ref="dropoffTimeRef"
						key="dropoff-time-input"
						v-model="payload.dropofftime"
						label-name="Drop off time"
						:error="payload.errors.dropofftime"
						:show-label="false"
						:options="dropoffTimeOptions"
						max-height="max-h-[260px]"
					/>
				</div>
				<hr class="dotted" />
				<div class="mt-2 w-full flex flex-col lg:flex-row gap-2">
					<FormSelectbox
						ref="ageRef"
						key="age-input"
						v-model="payload.ageid"
						label-name="Driver age"
						:error="payload.errors.ageid"
						class="basis-2/4"
						:show-label="false"
						:options="booking.age"
					/>
					<div class="w-full flex flex-col lg:flex-row gap-2">
						<InputBox
							key="promo-input"
							ref="campaigncodeRef"
							v-model="payload.campaigncode"
							:error="payload.errors.campaigncode"
							label-name="Promo code if any"
							class="w-full lg:grow lg:basis-2/3"
							:maxlength="10"
							:show-label="false"
							:disabled="false"
							:is-capitalised="true"
						/>
						<ButtonCta
							type="submit"
							button-mode="dark"
							button-radius="small"
							additional-classes="__submit h-[50px]"
							:is-disabled="
								booking.getIsSubmitting || isFormInvalid
							"
							:show-loader="booking.getIsSubmitting"
						>
							<span class="font-bold text-white">
								{{ booking.getSearchButtonText }}
							</span>
						</ButtonCta>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>
